import { recommendSetup } from "../../../configurator";
import type { StandardEquipmentCatalogue, RecommendationOptionsInput, RecommendSetupInput } from "../../../configurator";
import {
  buildConfigurationModalInput,
  convertToMm,
  type ConfigurationModalFormValues,
  type SavedCoilConfiguration,
} from "../../../web-app";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function buildSavedConfigurationFromModal(
  values: ConfigurationModalFormValues,
  mode: "create" | "edit",
  editId: string | null,
  catalogue: StandardEquipmentCatalogue,
  assumptions: RecommendationOptionsInput,
  existingCreatedAt?: Date,
): SavedCoilConfiguration {
  const recommendInput = {
    ...buildConfigurationModalInput(values, catalogue),
    recommendationOptions: assumptions,
  };
  const recommendation = recommendSetup(recommendInput);

  const ambientTemperatureC = Number(values.modalAmbientTemperatureC);
  const startingTemperatureStr = values.modalStartingTemperatureC.trim();
  const appProductName = values.modalApplicationProductName.trim();
  const subsequentTempStr = values.modalSubsequentProcessTemperatureC.trim();

  const config: SavedCoilConfiguration = {
    id: mode === "edit" && editId ? editId : `config-${Date.now()}`,
    reference: values.configReference.trim(),
    project: values.configProject.trim(),
    selectedPowerSourceId: values.modalSelectedPowerSourceId,
    fieldJointGeometry: {
      pipeOutsideDiameterMm: convertToMm(
        Number(values.modalPipeOutsideDiameter),
        values.modalPipeOutsideDiameterUnit,
      ),
      pipeWallThicknessMm: convertToMm(
        Number(values.modalPipeWallThickness),
        values.modalPipeWallThicknessUnit,
      ),
      factoryCoatingThicknessMm: convertToMm(
        Number(values.modalFactoryCoatingThickness),
        values.modalFactoryCoatingThicknessUnit,
      ),
      sideACutbackMm: convertToMm(Number(values.modalSideACutback), values.modalSideACutbackUnit),
      sideBCutbackMm: convertToMm(Number(values.modalSideBCutback), values.modalSideBCutbackUnit),
    },
    heating: {
      ambientTemperatureC: Number.isFinite(ambientTemperatureC) ? ambientTemperatureC : 20,
      ...(startingTemperatureStr !== "" && Number.isFinite(Number(startingTemperatureStr))
        ? { startingTemperatureC: Number(startingTemperatureStr) }
        : {}),
      targetHeatTemperatureC: Number(values.modalTargetHeatTemperatureC),
    },
    recommendation,
    createdAt: existingCreatedAt ?? new Date(),
  };

  if (appProductName !== "") {
    config.applicationProduct = {
      id: slugify(appProductName),
      name: appProductName,
      ...(subsequentTempStr !== "" && Number.isFinite(Number(subsequentTempStr))
        ? { subsequentProcessTemperatureC: Number(subsequentTempStr) }
        : {}),
    };
  }

  return config;
}

export function recalculateSavedConfiguration(
  config: SavedCoilConfiguration,
  catalogue: StandardEquipmentCatalogue,
  assumptions: RecommendationOptionsInput,
): SavedCoilConfiguration {
  const recommendInput: RecommendSetupInput = {
    selectedPowerSourceId: config.selectedPowerSourceId,
    fieldJointGeometry: { ...config.fieldJointGeometry },
    heating: { ...config.heating },
    ...(config.applicationProduct !== undefined
      ? { applicationProduct: { ...config.applicationProduct } }
      : {}),
    recommendationOptions: assumptions,
    standardEquipmentCatalogue: catalogue,
  };

  const recommendation = recommendSetup(recommendInput);
  return { ...config, recommendation };
}

export function defaultPowerSourceId(catalogue: StandardEquipmentCatalogue): string {
  const preferred = catalogue.powerSources.find((ps) => ps.id === "ps-120");
  return preferred?.id ?? catalogue.powerSources[0]?.id ?? "";
}

export function formValuesFromConfiguration(config: SavedCoilConfiguration): ConfigurationModalFormValues {
  return {
    configReference: config.reference,
    configProject: config.project,
    modalSelectedPowerSourceId: config.selectedPowerSourceId,
    modalPipeOutsideDiameter: String(config.fieldJointGeometry.pipeOutsideDiameterMm),
    modalPipeOutsideDiameterUnit: "mm",
    modalPipeWallThickness: String(config.fieldJointGeometry.pipeWallThicknessMm),
    modalPipeWallThicknessUnit: "mm",
    modalFactoryCoatingThickness: String(config.fieldJointGeometry.factoryCoatingThicknessMm * 1000),
    modalFactoryCoatingThicknessUnit: "um",
    modalSideACutback: String(config.fieldJointGeometry.sideACutbackMm),
    modalSideACutbackUnit: "mm",
    modalSideBCutback: String(config.fieldJointGeometry.sideBCutbackMm),
    modalSideBCutbackUnit: "mm",
    modalAmbientTemperatureC: String(config.heating.ambientTemperatureC),
    modalStartingTemperatureC:
      config.heating.startingTemperatureC != null ? String(config.heating.startingTemperatureC) : "",
    modalTargetHeatTemperatureC: String(config.heating.targetHeatTemperatureC),
    modalApplicationProductName: config.applicationProduct?.name ?? "",
    modalSubsequentProcessTemperatureC:
      config.applicationProduct?.subsequentProcessTemperatureC != null
        ? String(config.applicationProduct.subsequentProcessTemperatureC)
        : "",
  };
}

export function emptyConfigurationFormValues(
  catalogue: StandardEquipmentCatalogue,
): ConfigurationModalFormValues {
  return {
    configReference: "",
    configProject: "",
    modalSelectedPowerSourceId: defaultPowerSourceId(catalogue),
    modalPipeOutsideDiameter: "",
    modalPipeOutsideDiameterUnit: "mm",
    modalPipeWallThickness: "",
    modalPipeWallThicknessUnit: "mm",
    modalFactoryCoatingThickness: "",
    modalFactoryCoatingThicknessUnit: "um",
    modalSideACutback: "",
    modalSideACutbackUnit: "mm",
    modalSideBCutback: "",
    modalSideBCutbackUnit: "mm",
    modalAmbientTemperatureC: "",
    modalStartingTemperatureC: "",
    modalTargetHeatTemperatureC: "",
    modalApplicationProductName: "",
    modalSubsequentProcessTemperatureC: "",
  };
}
